import { axios } from "../api";
import { User } from "../users/user-management/types";
import { DueDateDetailView, DueDateLinkedRecord } from "./type";
import { DUE_WINDOW_FIELD, dueWindowSpecs, isDueWindow } from "./dueWindows";
import { ASSIGNEE_FIELD, FILTER_COLUMN } from "./whiteboardFilters";

export type DueDateStatusLabelLookup = (
  status: number | string | null | undefined
) => string;

export function createDueDateStatusLabelLookup(
  dueDateConfig: { status?: Record<number, string> } | null | undefined,
  fallbackStatus?: string
): DueDateStatusLabelLookup {
  return (recordStatus) => {
    if (typeof recordStatus === "number" && dueDateConfig?.status) {
      return (
        dueDateConfig.status[recordStatus]?.toUpperCase() ??
        String(recordStatus)
      );
    }
    if (typeof recordStatus === "string" && recordStatus) {
      return recordStatus.toUpperCase();
    }
    if (typeof fallbackStatus === "string" && fallbackStatus) {
      return fallbackStatus.toUpperCase();
    }
    return recordStatus != null ? String(recordStatus) : "";
  };
}

export async function fetchDueDatePdfContext(
  duedateId: number,
  statusLabelFor: DueDateStatusLabelLookup
) {
  const [duedateResp, linkedRecordsResp] = await Promise.all([
    axios.get<DueDateDetailView>(`/v6/duedate/${duedateId}`, {
      id: `get-duedate-${duedateId}`,
    }),
    axios.get<DueDateLinkedRecord[]>(
      `/v6/duedate/linked_records/${duedateId}`,
      {
        id: `get-duedate-linked-records-${duedateId}`,
      }
    ),
  ]);

  return buildDueDatePdfContext(
    duedateResp.data.last_done,
    linkedRecordsResp.data,
    statusLabelFor
  );
}

export const mapDueDateLinkedRecordForPdf = (
  record: DueDateLinkedRecord,
  statusLabelFor: DueDateStatusLabelLookup
) => ({
  last_done: record.last_done ?? "",
  due_date: record.required ?? record.due_date ?? "",
  status: statusLabelFor(record.status),
  title: record.title ?? "",
  section: record.section?.name ?? "",
  type_id: record.type_obj?.name ?? "",
  created_by: record.user_obj?.gecos ?? "",
  company_id: record.company_obj?.name ?? "",
  comment: record.comment ?? "",
});

export const buildDueDatePdfContext = (
  reportDate: string | Date | null | undefined,
  linkedRecords: DueDateLinkedRecord[],
  statusLabelFor: DueDateStatusLabelLookup
) => ({
  report_date: reportDate ?? "",
  lines: linkedRecords.map((record) =>
    mapDueDateLinkedRecordForPdf(record, statusLabelFor)
  ),
});

export const formtListObject = (data:User[])=>{
    const formatedData = data.map((obj)=>{
        return {
            "contact_id" : obj.id,
            "contact_type":"user",
        }
    })
    return formatedData
}

type DueDateFilterSpec = {
  field: string;
  op: string;
  value: unknown;
  model?: string;
};

const DATE_TYPE_TO_COLUMN: Record<string, string> = {
  required: "required",
  last_done: "last_done",
  begin_date: "begin_date",
};

/**
 * Map the grid's filter items (Filters panel, Quick Filters, advanced search) to what GET
 * /v6/duedate applies: real whiteboard columns (a joined-name column → its id column,
 * whiteboardFilters.ts FILTER_COLUMN), the Remaining window → `required` ranges, the advanced
 * search's date range → its column, and Assigned To "is" one user → the route's own
 * `assigned_to` param (which also matches the record's recipients, multi-assign tenants).
 */
export function transformDueDateListParams(
  params: Record<string, unknown>,
  today: Date = new Date()
): Record<string, unknown> {
  const rawFilters = params.filter;
  if (!Array.isArray(rawFilters) || rawFilters.length === 0) {
    return params;
  }

  const parsed: DueDateFilterSpec[] = rawFilters.map((item) =>
    typeof item === "string" ? JSON.parse(item) : item
  );

  let dateType = "";
  let dateFrom: string | null = null;
  let dateTo: string | null = null;
  let assignee: unknown = undefined;
  const kept: DueDateFilterSpec[] = [];

  for (const spec of parsed) {
    if (spec.field === ASSIGNEE_FIELD && !spec.model && spec.op === "is") {
      if (spec.value != null && spec.value !== "") {
        assignee = spec.value;
      }
      continue;
    }
    if (spec.field === "date_type") {
      if (spec.value != null && spec.value !== "") {
        dateType = String(spec.value);
      }
      continue;
    }
    if (spec.field === "date_from") {
      if (spec.value != null && spec.value !== "") {
        dateFrom = String(spec.value);
      }
      continue;
    }
    if (spec.field === "date_to") {
      if (spec.value != null && spec.value !== "") {
        dateTo = String(spec.value);
      }
      continue;
    }
    // Remaining is computed, not a column: its filter is a due WINDOW (a
    // Dashboard tile's, BP-93) and goes to the route as `required` specs.
    if (spec.field === DUE_WINDOW_FIELD) {
      if (isDueWindow(spec.value)) {
        kept.push(...dueWindowSpecs(spec.value, today));
      }
      continue;
    }
    const column = spec.model ? undefined : FILTER_COLUMN[spec.field];
    kept.push(column ? { ...spec, field: column } : spec);
  }

  if (dateFrom || dateTo) {
    const dateField =
      DATE_TYPE_TO_COLUMN[dateType || "required"] ?? "required";
    if (dateFrom) {
      kept.push({ field: dateField, op: ">=", value: dateFrom });
    }
    if (dateTo) {
      kept.push({ field: dateField, op: "<=", value: dateTo });
    }
  }

  const next = { ...params };
  if (assignee !== undefined) {
    next[ASSIGNEE_FIELD] = assignee;
  }
  if (kept.length > 0) {
    next.filter = kept.map((spec) => JSON.stringify(spec));
  } else {
    delete next.filter;
  }
  return next;
}


/** whiteboard_assign_multi_user = yes: Assigned To is a multi-user field over whiteboard_recipients. */
export const isMultiAssign = (config?: Record<string, unknown> | null): boolean =>
  config?.assign_multi_user === "yes";

/**
 * The multi Assigned To field's value for a loaded or saved record: its recipients, else its single
 * assignee (a record that has assigned_to but no recipient rows), else none.
 */
export const assigneesForForm = (data: {
  recipients?: { id?: number | null }[] | null;
  assigned_obj?: { id?: number | null } | null;
}): { id?: number | null }[] => {
  if (data.recipients && data.recipients.length > 0) return data.recipients;
  if (data.assigned_obj && data.assigned_obj.id) return [data.assigned_obj];
  return [];
};
